import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "../services/axiosWrapper";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "../services/constants";
import { showToastNotification } from "../reducers/toastSlice";
import { t, untranslated } from "@i18n";
import type {
  AddProjectRoleRequest,
  AddProjectRoleResponse,
  DeleteProjectRoleRequest,
  DeleteProjectRoleResponse,
  ChangeProjectRolePermissionRequest,
  ChangeProjectRolePermissionResponse,
  GetProjectRolesRequest,
  GetProjectRolesResponse,
  ChangeUserRoleFromProjectRequest,
  ChangeUserRoleFromProjectResponse,
  RemoveUsersFromSingleProjectRequest,
  RemoveUsersFromSingleProjectResponse,
  GetProjectObjectResponse,
  TeamsControllerInsertRequest,
  TeamsControllerUpdateRequest,
  TeamsControllerDeleteRequest,
  TeamsControllerGetRequest,
  TeamsControllerResponse,
  LocationControllerInsertRequest,
  LocationControllerUpdateRequest,
  LocationControllerDeleteRequest,
  LocationControllerReorderRequest,
  LocationControllerGetRequest,
  LocationControllerResponse,
  WbsControllerInsertRequest,
  WbsControllerUpdateRequest,
  WbsControllerDeleteRequest,
  WbsControllerGetRequest,
  WbsControllerResponse,
} from "@ServerTypes/projects/project.schema";
import type {
  SaveDefaultSpaceRequest,
  SaveDefaultSpaceResponse,
  SpacesDataRequest,
  SpacesDataResponse,
} from "@ServerTypes/spaces/space.schema";
import type {
  ProjectTemplateConfigHandlerRequest,
  ProjectTemplateConfigHandlerResponse,
} from "@ServerTypes/forms/forms.schema";
import type {
  EditVisibleDataRequest,
  EditVisibleDataResponse,
  SaveStatusDataRequest,
  SaveStatusDataResponse,
  GetStatusDataRequest,
  GetStatusDataResponse,
  GetCategoryDataResponse,
} from "@ServerTypes/status/status.schema";
import {
  setProjectRolesList,
  setCategoryListAction as setCategoryList,
  setStatusData,
  deletedSpaceStatus,
  getProjectSettingObjectAction as getProjectSettingObject,
} from "../reducers/projectSetting";

export async function addProjectRole(data: AddProjectRoleRequest): Promise<AddProjectRoleResponse> {
  const response = await axiosWrapper.post<AddProjectRoleResponse>(`${getBackendUrl()}/projects/addProjectRole`, data);
  return response.data;
}

export async function deleteProjectRole(data: DeleteProjectRoleRequest): Promise<DeleteProjectRoleResponse> {
  const response = await axiosWrapper.post<DeleteProjectRoleResponse>(
    `${getBackendUrl()}/projects/deleteProjectRole`,
    data,
  );
  return response.data;
}

// updateProjectRolePermission
export function updateProjectRolePermission(data: ChangeProjectRolePermissionRequest) {
  return () => {
    axiosWrapper.post<ChangeProjectRolePermissionResponse>(
      `${getBackendUrl()}/projects/changeProjectRolePermission`,
      data,
    );
  };
}

export function getProjectRoles(data: { projectId?: string }) {
  return (dispatch: AppDispatch) => {
    let projectId = data?.projectId;
    if (!projectId) {
      const pathArray = location.pathname.split("/");
      projectId = pathArray[pathArray.length - 2];
    }
    const requestData: GetProjectRolesRequest = { id: projectId };
    axiosWrapper
      .post<GetProjectRolesResponse>(`${getBackendUrl()}/projects/getProjectRoles`, requestData)
      .then((response) => {
        if (response.data.status) {
          dispatch(setProjectRolesList(response.data.data));
        }
      });
  };
}

export function saveProjectRole(
  data: ChangeUserRoleFromProjectRequest,
  toastdata: { selectedUserfromRow: string; roleChangedTo: string },
) {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .post<ChangeUserRoleFromProjectResponse>(`${getBackendUrl()}/projects/changeUserRoleFromProject/`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(
            showToastNotification({
              content: t("{{user}}'s role has been changed to {{role}}", {
                user: toastdata.selectedUserfromRow,
                role: toastdata.roleChangedTo,
              }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        } else {
          dispatch(
            showToastNotification({
              content: untranslated(response.data.errorMessage ?? "Something went wrong"),
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
      });
  };
}
// updateStatusSpaces
export function updateStatusSpaces(data: EditVisibleDataRequest) {
  return () => {
    axiosWrapper.post<EditVisibleDataResponse>(`${getBackendUrl()}/status/editVisibleData`, data);
  };
}
// addStatusToProject
export function addStatusToProject(data: SaveStatusDataRequest) {
  return () => {
    axiosWrapper.post<SaveStatusDataResponse>(`${getBackendUrl()}/status/saveStatusData`, data);
  };
}
// getStatusData
export function getStatusData(data: GetStatusDataRequest) {
  return (dispatch: AppDispatch) => {
    axiosWrapper.post<GetStatusDataResponse>(`${getBackendUrl()}/status/getStatusData`, data).then((response) => {
      if (response.data.status) {
        dispatch(setStatusData(response.data.data));
      }
    });
  };
}
// getAllCategories
export function getAllCategories() {
  return (dispatch: AppDispatch) => {
    axiosWrapper.get<GetCategoryDataResponse>(`${getBackendUrl()}/status/getCategoryData`).then((response) => {
      if (response.data.status) {
        dispatch(setCategoryList(response.data.data));
      }
    });
  };
}
// get list of user which are added into project

// export function deleteSpace(data) {
//   return async (dispatch: AppDispatch) => {
//     const response = await axiosWrapper.post(
//       `${getBackendUrl()}/spaces/deleteSpacesData`,
//       data
//     );

//     dispatch(deletedSpaceStatus(response.data.data));
//     return response.data.data.status;
//   };
// }

// delete space from project
export function deleteSpace(data: { spaceId: string }) {
  return (dispatch: AppDispatch) => {
    const requestData: SpacesDataRequest = {
      action: "remove",
      key: { spaceId: data.spaceId },
    };
    axiosWrapper.post<SpacesDataResponse>(`${getBackendUrl()}/spaces/spacesData`, requestData).then((response) => {
      dispatch(deletedSpaceStatus(response.data.result));
    });
  };
}

// Delete User from a project

export async function removeUsersFromSingleProject(
  data: RemoveUsersFromSingleProjectRequest,
): Promise<RemoveUsersFromSingleProjectResponse> {
  const response = await axiosWrapper.post<RemoveUsersFromSingleProjectResponse>(
    `${getBackendUrl()}/projects/removeUsersFromSingleProject/`,
    data,
  );
  return response.data;
}

export function getProjectObject(data: { id?: string }) {
  if (data.id) {
    return (dispatch: AppDispatch) => {
      axiosWrapper
        .get<GetProjectObjectResponse>(`${getBackendUrl()}/projects/getProjectObject/${data.id}`)
        .then((res) => {
          if (res.data.status) {
            dispatch(getProjectSettingObject(res.data.data));
          }
        })
        .catch((err: unknown) => {
          console.log("Error in project setting object ", err);
          Promise.reject(err);
        });
    };
  }
}
export type TeamsControllerRequest =
  | TeamsControllerInsertRequest
  | TeamsControllerUpdateRequest
  | TeamsControllerDeleteRequest
  | TeamsControllerGetRequest;

export const teamsController = async (data: TeamsControllerRequest): Promise<{ data: TeamsControllerResponse }> => {
  const response = await axiosWrapper.post<TeamsControllerResponse>(
    `${getBackendUrl()}/projects/teamsController`,
    data,
  );
  return response;
};

export type LocationControllerRequest =
  | LocationControllerInsertRequest
  | LocationControllerUpdateRequest
  | LocationControllerDeleteRequest
  | LocationControllerReorderRequest
  | LocationControllerGetRequest;

export const locationController = async (
  data: LocationControllerRequest,
): Promise<{ data: LocationControllerResponse }> => {
  const response = await axiosWrapper.post<LocationControllerResponse>(
    `${getBackendUrl()}/projects/locationController`,
    data,
  );
  return response;
};

export const projectTemplateConfigHandler = async (
  data: ProjectTemplateConfigHandlerRequest,
): Promise<{ data: ProjectTemplateConfigHandlerResponse }> => {
  const response = await axiosWrapper.post<ProjectTemplateConfigHandlerResponse>(
    `${getBackendUrl()}/forms/projectTemplateConfigHandler`,
    data,
  );
  return response;
};

export type WBSControllerRequest =
  | WbsControllerInsertRequest
  | WbsControllerUpdateRequest
  | WbsControllerDeleteRequest
  | WbsControllerGetRequest;

export const WBSController = async (data: WBSControllerRequest): Promise<{ data: WbsControllerResponse }> => {
  const response = await axiosWrapper.post<WbsControllerResponse>(`${getBackendUrl()}/projects/WBSController`, data);
  return response;
};
export const saveDefaultSpace = async (data: SaveDefaultSpaceRequest): Promise<{ data: SaveDefaultSpaceResponse }> => {
  return await axiosWrapper.post<SaveDefaultSpaceResponse>(`${getBackendUrl()}/spaces/saveDefaultSpace`, data);
};
